import { useEffect, useState, type RefObject } from 'react';
import { onScrollScenesActivated, scrollScenesActivated } from '../../../shared/lib/scroll-scenes';

/**
 * Flips true, and stays true, once an element comes within `margin` of the
 * viewport. Building a pinned ScrollTrigger forces a full layout pass, so the
 * triggers below the fold wait rather than all measuring during page load.
 *
 * A jump to a section cannot wait for that: it would aim at a document that is
 * still growing. `activateScrollScenes` flips every one of these at once.
 */
export function useNearViewport(ref: RefObject<HTMLElement | null>, margin = '100% 0px'): boolean {
  const [near, setNear] = useState(scrollScenesActivated);

  useEffect(() => {
    if (near) {
      return;
    }

    return onScrollScenesActivated(() => setNear(true));
  }, [near]);

  useEffect(() => {
    const element = ref.current;

    if (near || !element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
        }
      },
      { rootMargin: margin },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [margin, near, ref]);

  return near;
}
